/**
 * SentimentCard - Displays user sentiment data from review platforms
 * Pure presentational component wrapped with React.memo
 */

import { memo } from 'react';
import type { UserSentiment } from '../../../types';
import { platformLabels } from './constants';

interface SentimentCardProps {
  sentiment: UserSentiment;
}

function SentimentCardComponent({ sentiment }: SentimentCardProps) {
  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="font-medium text-gray-900">
          {platformLabels[sentiment.platform] || sentiment.platform}
        </span>
        {sentiment.rating !== undefined && (
          <div className="flex items-center gap-1">
            <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            <span className="text-lg font-semibold">{sentiment.rating.toFixed(1)}</span>
            {sentiment.review_count !== undefined && (
              <span className="text-sm text-gray-500">({sentiment.review_count} reviews)</span>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        {sentiment.top_pros.length > 0 && (
          <div>
            <div className="text-xs font-medium text-green-700 mb-1">Pros</div>
            <ul className="space-y-1">
              {sentiment.top_pros.slice(0, 3).map((pro) => (
                <li key={pro} className="text-sm text-gray-600 flex items-start gap-1">
                  <span className="text-green-500 mt-1">+</span>
                  {pro}
                </li>
              ))}
            </ul>
          </div>
        )}
        {sentiment.top_cons.length > 0 && (
          <div>
            <div className="text-xs font-medium text-red-700 mb-1">Cons</div>
            <ul className="space-y-1">
              {sentiment.top_cons.slice(0, 3).map((con) => (
                <li key={con} className="text-sm text-gray-600 flex items-start gap-1">
                  <span className="text-red-500 mt-1">-</span>
                  {con}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {sentiment.url && (
        <a
          href={sentiment.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center text-xs text-primary-600 hover:text-primary-800"
        >
          View all reviews
          <svg className="ml-1 h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
            />
          </svg>
        </a>
      )}
    </div>
  );
}

export const SentimentCard = memo(SentimentCardComponent);
